import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import AnimeCard from "@/components/feature/AnimeCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionTitle from "@/components/feature/SectionTitle";
import { usePageMeta } from "@/hooks/usePageMeta";
import { animeList } from "@/mocks/anime";
import { categoryShortcuts } from "@/mocks/categories";
import { featuredAnime, hotAnime, latestNews } from "@/utils/content";

export default function Home() {
  usePageMeta({
    title: "58动漫 - 最新动漫资讯与动画作品展示平台",
    description:
      "58动漫致力于为动漫爱好者提供丰富的动画作品展示及最新动漫资讯，包括热门动漫推荐、作品介绍和行业动态，满足用户观看和了解动漫的需求。",
    keywords: "58动漫,动漫作品,动画资讯,热门动漫,动画推荐,动漫动态,漫画作品,动画频道",
  });

  const heroItems = featuredAnime;
  const news = latestNews(6);
  const ranking = hotAnime(8);
  const recommended = animeList.filter((item) => ["治愈", "奇幻", "科幻"].some((t) => item.tags.includes(t))).slice(0, 8);

  return (
    <div className="w-full">
      <h1 className="sr-only">58动漫 - 最新动漫资讯与动画作品展示平台</h1>
      <HeroCarousel items={heroItems} />

      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 xl:px-12">
        <section className="py-10 md:py-12" aria-label="动漫分类快捷入口">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {categoryShortcuts.map((item, index) => (
              <Link
                key={item.key}
                to={`/category/${item.type}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:bg-primary-50/40 md:p-5"
              >
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-2xl ${
                    index % 2 === 0
                      ? "bg-primary-100 text-primary-600"
                      : "bg-accent-100 text-accent-700"
                  }`}
                >
                  <i className={item.icon} />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                    {item.label}
                  </span>
                  <span className="mt-0.5 hidden truncate text-xs text-foreground-500 sm:block">{item.desc}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="pb-10 md:pb-12" aria-label="精选动画作品">
          <SectionTitle
            title="精选动画作品"
            subtitle="编辑精挑细选的优质作品，涵盖多种题材"
            icon="ri-movie-2-line"
            moreTo="/category/推荐"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {recommended.map((item) => (
              <AnimeCard key={item.id} anime={item} />
            ))}
          </div>
        </section>

        <section className="pb-10 md:pb-12" aria-label="最新动漫资讯与热门排行">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-6">
            <div className="lg:col-span-2">
              <SectionTitle
                title="最新动漫资讯"
                subtitle="行业动态、作品资讯与展会信息每日更新"
                icon="ri-newspaper-line"
                moreTo="/news"
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {news.map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>
            </div>

            <div>
              <SectionTitle title="人气作品榜" icon="ri-fire-line" moreTo="/category/热门" moreLabel="完整榜单" />
              <ol className="flex flex-col gap-2.5 rounded-lg border border-background-200 bg-background-50 p-3">
                {ranking.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-primary-50/60"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 58动漫人气作品`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="truncate text-[11px] text-foreground-500">
                          {item.year} · {item.types.slice(0, 2).join(" / ")}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1 text-xs font-bold text-primary-600">
                        <i className="ri-star-fill text-[11px]" />
                        {item.rating?.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      </div>

      <section className="border-y border-background-200/70 bg-background-100 py-12 md:py-16" aria-label="网站简介">
        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-center gap-8 px-4 md:px-8 lg:grid-cols-2 lg:gap-12 xl:px-12">
          <div className="relative h-[240px] w-full overflow-hidden rounded-lg md:h-[320px]">
            <img
              src="https://readdy.ai/api/search-image?query=Anime%20style%20digital%20illustration%20of%20a%20cozy%20fan%20room%20filled%20with%20colorful%20animation%20posters%2C%20figures%20on%20shelves%2C%20a%20glowing%20screen%20and%20warm%20desk%20lamp%2C%20soft%20evening%20light%2C%20cel-shaded%2C%20clean%20simple%20background%2C%20high%20detail%2C%20no%20text&width=900&height=700&seq=about-58anime&orientation=squarish"
              alt="58动漫 内容展示"
              title="58动漫 - 最新动漫资讯与动画作品展示平台"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/30 to-transparent" />
          </div>

          <div>
            <h2 className="font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
              关于 <span className="text-primary-500">58动漫</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              58动漫致力于为动漫爱好者提供丰富的动画作品展示及最新动漫资讯，包括热门动漫推荐、作品介绍和行业动态，满足用户观看和了解动漫的需求。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              我们每日整理行业动态、作品资讯与展会信息，并通过多维度筛选与关键词搜索，帮助你快速找到想看的作品、了解正在发生的动漫圈大事。
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: `${animeList.length}+`, label: "收录作品", tone: "primary" },
                { value: "10+", label: "资讯栏目", tone: "accent" },
                { value: "每日", label: "内容更新", tone: "secondary" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 px-3 py-4 text-center"
                >
                  <p
                    className={`font-heading text-xl font-extrabold md:text-2xl ${
                      stat.tone === "primary"
                        ? "text-primary-600"
                        : stat.tone === "accent"
                          ? "text-accent-600"
                          : "text-secondary-700"
                    }`}
                  >
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/最新"
                className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-compass-3-line text-base" />
                浏览全部作品
              </Link>
              <Link
                to="/news"
                className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-newspaper-line text-base" />
                查看最新资讯
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}